import { apiErrorMessage } from '@agos/api-client'
import {
  getGetNotificationsChannelsQueryKey,
  getGetNotificationsPushMessagesQueryKey,
  useGetNotificationsChannelSettings,
  useGetNotificationsChannels,
  useGetNotificationsChannelsCoverage,
  useGetNotificationsPushMessages,
  usePutNotificationsChannelsEventKey,
  usePutNotificationsPushMessagesMessageKey,
} from '@agos/api-client/notifications'
import { T, formatCount, tt, useAuth } from '@agos/shared'
import { ConfirmDialog, PageHeader, Spinner, cn, useToast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Bell,
  ChevronDown,
  ChevronUp,
  Info,
  Mail,
  MessageSquare,
  RotateCcw,
  Save,
  Smartphone,
} from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  activeCount,
  channelLabel,
  eventsOf,
  grouped,
  masterMessage,
  masterState,
  mastersOf,
  modeLabel,
  modeMessage,
  orderMessage,
  overriddenChannels,
  payloadOf,
  reachMessage,
  reachNeedsSaying,
  reachOf,
  wouldSilence,
  type PushCoverage,
  type Channel,
  type EventChannels,
  type Mode,
} from './notification-channels'
import { DeliveryLog } from './DeliveryLog'
import {
  draftOf,
  isDirty,
  isOverridden,
  placeholderHint,
  previewOf,
  unknownPlaceholders,
  type TemplateDraft,
} from '../message-templates'
import {
  payloadOf as pushPayloadOf,
  pushMessagesOf,
  type PushMessage,
} from './push-messages'

/**
 * `/settings-notifications` — which channels carry each event.
 *
 * **FB-144.** Two of the nine events are configured with push alone. Push needs a subscription the customer
 * created on the website, so a push-only event reaches those customers and nobody else — while legacy shows
 * *1/1 aktívnych* and a green chip, and its own info box promises that the system "automaticky padne na
 * email alebo SMS" when the customer has no device. There is nothing to fall back to when push is the only
 * channel enabled.
 *
 * **FB-145.** Legacy saves on every click, optimistically, and a refused save leaves the flipped chip on
 * screen with only a toast to say otherwise. Here the change is applied when the server accepts it and
 * rolled back when it does not.
 *
 * **And the master switches.** `NotificationDispatcher` has a per-channel switch that overrides every row
 * here; a green chip for a centrally-disabled channel would be a lie. It is read and shown. The rest of the
 * §10A.4 surface — the third strategy, the channel order, the delivery log — is a §9.5 build.
 *
 * Ported from legacy `pages/settings-notifications.tsx` (213 lines).
 */

export function SettingsNotificationsPage() {
  const channels = useGetNotificationsChannels()
  const masters = useGetNotificationsChannelSettings()

  if (channels.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return <ChannelEditor channels={channels.data} masters={masters.data} />
}

interface PendingSilence {
  event: EventChannels
  channel: Channel
}

function ChannelEditor({ channels, masters }: { channels: unknown; masters: unknown }) {
  // FB-144. Its own endpoint, so a page that does not need the figure does not pay three aggregate
  // counts over the customer table for it. `null` until it answers — a percentage of nothing is worse
  // than saying the server is not reporting one.
  const { data: coverageData } = useGetNotificationsChannelsCoverage()
  const coverage: PushCoverage | null =
    coverageData === undefined
      ? null
      : {
          subscribers: Number(coverageData.pushReachable ?? 0),
          customers: Number(coverageData.totalCustomers ?? 0),
        }

  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [events, setEvents] = useState<EventChannels[]>(() => eventsOf(channels))
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingSilence | null>(null)

  const channelMasters = mastersOf(masters)
  const writable = mayWrite('notifications', user?.systemRole)
  const readOnly = readOnlyReason('notifications', user?.systemRole)

  const save = usePutNotificationsChannelsEventKey()

  /**
   * FB-145. The change lands when the server takes it, not before.
   *
   * Legacy sets state, fires the PUT and keeps the new state whatever comes back — so a refused save leaves
   * the screen disagreeing with the server about who gets told what.
   */
  const apply = (event: EventChannels, patch: Partial<EventChannels>) => {
    const next: EventChannels = { ...event, ...patch }
    setSavingKey(event.key)
    save.mutate(
      { eventKey: event.key, data: { ...payloadOf(next) } },
      {
        onSuccess: () => {
          setEvents((current) => current.map((one) => (one.key === event.key ? next : one)))
          void queryClient.invalidateQueries({ queryKey: getGetNotificationsChannelsQueryKey() })
          setSavingKey(null)
        },
        onError: (caught) => {
          setSavingKey(null)
          toast({
            variant: 'destructive',
            title: tt('Zmena sa neuložila'),
            description: apiErrorMessage(
                           caught,
                           tt('Na serveri sa nič nezmenilo - na obrazovke tiež nie.'),
                         ),
          })
        },
      },
    )
  }

  const toggleChannel = (event: EventChannels, channel: Channel) => {
    const next = !event[channel]
    if (wouldSilence(event, channel, next)) {
      setPending({ event, channel })
      return
    }
    apply(event, { [channel]: next })
  }

  const sections = grouped(events)

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Oznámenia a kanály')}
        description={tt('Ktorým kanálom sa každá udalosť dostane k zákazníkovi')}
        icon={<Bell className="w-5 h-5" />}
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      <div className="rounded-2xl border border-border bg-card px-4 py-3 space-y-2" data-how-it-works>
        <p className="text-sm font-semibold flex items-center gap-2">
          <Info className="w-4 h-4 text-primary" /> <T>Ako sa oznámenie posiela</T>
        </p>
        <p className="text-xs text-muted-foreground" data-channel-order>
          {orderMessage()}
        </p>
        <p className="text-xs text-muted-foreground">
          <strong>{modeLabel('fallback')}</strong> - {modeMessage('fallback')}
        </p>
        <p className="text-xs text-muted-foreground">
          <strong>{modeLabel('all')}</strong> - {modeMessage('all')}
        </p>
        {/* The master switches, which override every row below. */}
        <div className="flex flex-wrap gap-2 pt-1">
          {(['push', 'sms', 'email'] as const).map((channel) => {
            const state = masterState(channelMasters, channel)
            return (
              <span
                key={channel}
                data-master={channel}
                data-master-state={state}
                className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium',
                  state === 'on' && 'border-emerald-500/40 bg-emerald-500/5 text-emerald-500',
                  state === 'off' && 'border-red-500/40 bg-red-500/5 text-red-500',
                  state === 'degraded' && 'border-amber-500/40 bg-amber-500/5 text-amber-500',
                )}
              >
                {channelLabel(channel)}{' '}
                {state === 'on' ? <T>funguje</T> : state === 'off' ? <T>vypnuté</T> : <T>nefunkčné</T>}
              </span>
            )
          })}
        </div>
      </div>

      {sections.map((section) => {
        const isOpen = !collapsed.has(section.category)
        return (
          <div key={section.category} className="bg-card border border-border rounded-2xl overflow-hidden">
            <button
              type="button"
              data-category={section.category}
              onClick={() =>
                setCollapsed((current) => {
                  const next = new Set(current)
                  if (next.has(section.category)) next.delete(section.category)
                  else next.add(section.category)
                  return next
                })
              }
              className="w-full flex items-center gap-3 px-5 py-3 border-b border-border bg-muted/20"
            >
              <h2 className="font-bold text-foreground flex-1 text-left text-sm">{section.category}</h2>
              <span className="text-xs text-muted-foreground">
                {formatCount(activeCount(section.events))}/{formatCount(section.events.length)}{' '}
                <T>s aspoň jedným kanálom</T>
              </span>
              {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {isOpen && (
              <div className="divide-y divide-border">
                {section.events.map((event) => (
                  <EventRow
                    key={event.key}
                    event={event}
                    masters={channelMasters}
                    saving={savingKey === event.key}
                    disabled={!writable}
                    coverage={coverage}
                    onToggle={(channel) => toggleChannel(event, channel)}
                    onMode={(mode) => apply(event, { mode })}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}

      {/* §9.206 - "which channels" and "what do they say" are one question to whoever is asked it,
        * so the texts live under the switches rather than on a screen of their own. */}

      {/* §9.253 - and *did it actually go out* is the third question the same person asks, so the log
        * sits under the two that decide what is sent. Read-only: the writes this module also has
        * include turning a channel off everywhere, which is banked rather than built. */}
      <DeliveryLog />

      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        variant="danger"
        title={tt('Vypnúť posledný kanál?')}
        description={
          pending === null
            ? ''
            : `${pending.event.label}: ${tt('po vypnutí tohto kanála toto oznámenie nedostane nikto.')}`
        }
        confirmLabel={tt('Vypnúť')}
        icon={<AlertTriangle className="w-5 h-5" />}
        onConfirm={() => {
          if (pending === null) return
          const { event, channel } = pending
          setPending(null)
          apply(event, { [channel]: false })
        }}
      />
    </div>
  )
}

/**
 * The wording of each push notification — §9.206, `BACKEND.md §10A.83`.
 *
 * **The default is a placeholder, never a value.** An input pre-filled with the built-in text cannot
 * tell *"I left it alone"* from *"I typed exactly that"*, and the first save would freeze every
 * untouched message against a later improvement to the wording.
 *
 * **Saved on a button, not on every keystroke.** FB-145 is on this very page — every click was a
 * save, and a refused one stayed on screen. A title and a body are edited together and are wrong
 * apart: a half-saved message is one that goes out.
 */
/**
 * §9.298 - the owner moved this to its own tab: *"Text push oznámení. Move it to next to E-Mail as
 * «Pushup»"*. Exported rather than relocated, because `CHANNEL_ICON` and the placeholder helpers in
 * this file are shared with the event rows above - moving the function would have dragged them along
 * or duplicated them.
 */
export function PushMessageEditor({ writable }: { writable: boolean }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data, isLoading } = useGetNotificationsPushMessages()
  const save = usePutNotificationsPushMessagesMessageKey()
  const [drafts, setDrafts] = useState<Record<string, TemplateDraft>>({})
  const [savingKey, setSavingKey] = useState<string | null>(null)

  const messages = pushMessagesOf(data)

  const draftFor = (message: PushMessage) =>
    drafts[message.key] ?? draftOf(message)

  const submit = async (message: PushMessage, draft: TemplateDraft) => {
    setSavingKey(message.key)
    try {
      await save.mutateAsync({ messageKey: message.key, data: pushPayloadOf(draft) })
      await queryClient.invalidateQueries({ queryKey: getGetNotificationsPushMessagesQueryKey() })
      setDrafts((current) => {
        const next = { ...current }
        delete next[message.key]
        return next
      })
      toast({ title: tt('Text uložený'), description: message.label })
    } catch {
      // The draft stays exactly as typed. A refused save that clears the box loses the wording.
      toast({ variant: 'destructive', title: tt('Uloženie zlyhalo'), description: message.label })
    } finally {
      setSavingKey(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-push-texts={messages.length}>
      <div className="px-5 py-3 border-b border-border bg-muted/20">
        <h2 className="font-bold text-foreground text-sm flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-primary" /> <T>Texty push oznámení</T>
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          <T>
            Prázdne pole znamená pôvodný text. Rovnaké premenné ako pri SMS - vypíšu sa zo skutočnej
            objednávky.
          </T>
        </p>
      </div>

      <div className="divide-y divide-border">
        {messages.map((message) => {
          const draft = draftFor(message)
          const dirty = isDirty(message, draft)
          const preview = previewOf(message, draft)
          const strange = [
            ...unknownPlaceholders(draft.first, message.placeholders),
            ...unknownPlaceholders(draft.body, message.placeholders),
          ]

          return (
            <div key={message.key} className="px-5 py-4 space-y-2" data-push-message={message.key}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-foreground">{message.label}</span>
                {isOverridden(message) && (
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-primary/40 bg-primary/10 text-primary"
                    data-push-overridden
                  >
                    <T>vlastný text</T>
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{message.desc}</p>

              <input
                value={draft.first}
                placeholder={message.defaultFirst}
                aria-label={`${tt('Nadpis')} - ${message.label}`}
                disabled={!writable}
                onChange={(change) =>
                  setDrafts((current) => ({
                    ...current,
                    [message.key]: { ...draft, first: change.target.value },
                  }))
                }
                className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
              />
              <textarea
                value={draft.body}
                placeholder={message.defaultBody}
                aria-label={`${tt('Text')} - ${message.label}`}
                rows={2}
                disabled={!writable}
                onChange={(change) =>
                  setDrafts((current) => ({
                    ...current,
                    [message.key]: { ...draft, body: change.target.value },
                  }))
                }
                className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 resize-none disabled:opacity-50"
              />

              <p className="text-[11px] text-muted-foreground" data-push-placeholders>
                {placeholderHint(message.placeholders)}
              </p>
              {strange.length > 0 && (
                <p className="text-[11px] text-amber-500" data-push-unknown={strange.length}>
                  {tt('Toto sa nenahradí a odíde tak, ako je napísané')}:{' '}
                  {strange.map((name) => `{${name}}`).join(', ')}
                </p>
              )}

              {/* A template is a sentence with holes in it, and the mistakes are all in the holes. */}
              <div className="rounded-xl border border-border bg-muted/30 px-3 py-2" data-push-preview={message.key}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <T>Ukážka</T>
                </p>
                <p className="text-sm font-semibold text-foreground">{preview.first}</p>
                <p className="text-xs text-muted-foreground">{preview.body}</p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={!writable || !dirty || savingKey === message.key}
                  onClick={() => void submit(message, draft)}
                  data-push-save={message.key}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-semibold disabled:opacity-40"
                >
                  <Save className="w-3.5 h-3.5" /> <T>Uložiť text</T>
                </button>
                <button
                  type="button"
                  disabled={!writable || (!isOverridden(message) && draft.first === '' && draft.body === '')}
                  onClick={() => void submit(message, { first: '', body: '' })}
                  data-push-reset={message.key}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-muted-foreground text-xs font-semibold disabled:opacity-40"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> <T>Vrátiť pôvodný text</T>
                </button>
                {savingKey === message.key && <Spinner />}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const CHANNEL_ICON = { push: Bell, email: Mail, sms: MessageSquare } as const

function EventRow({
  event,
  masters,
  saving,
  disabled,
  coverage,
  onToggle,
  onMode,
}: {
  event: EventChannels
  masters: ReturnType<typeof mastersOf>
  saving: boolean
  disabled: boolean
  /** FB-144 — how many customers push reaches. `null` while the request is in flight. */
  coverage: PushCoverage | null
  onToggle: (channel: Channel) => void
  onMode: (mode: Mode) => void
}) {
  const overridden = overriddenChannels(event, masters)
  const reach = reachOf(event)

  return (
    <div className="px-5 py-4" data-event={event.key}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground text-sm">{event.label}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{event.desc}</p>
          <p className="text-[10px] font-mono text-muted-foreground/60 mt-1">{event.key}</p>
        </div>
        {saving && <Spinner />}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(['push', 'email', 'sms'] as const).map((channel) => {
          const Icon = CHANNEL_ICON[channel]
          const enabled = event[channel]
          return (
            <button
              key={channel}
              type="button"
              data-chip={channel}
              data-chip-state={enabled ? 'on' : 'off'}
              aria-pressed={enabled}
              aria-label={`${channelLabel(channel)} - ${event.label}`}
              disabled={disabled || saving}
              onClick={() => onToggle(channel)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-50',
                enabled
                  ? 'bg-primary/15 border-primary/40 text-primary'
                  : 'bg-muted border-border text-muted-foreground',
              )}
            >
              <Icon className="w-3.5 h-3.5" /> {channelLabel(channel)}
            </button>
          )
        })}
        <span className="text-muted-foreground/50 text-xs mx-1">•</span>
        <label className="text-xs text-muted-foreground" htmlFor={`mode-${event.key}`}>
          <T>Režim</T>
        </label>
        <select
          id={`mode-${event.key}`}
          data-mode={event.mode}
          disabled={disabled || saving}
          value={event.mode}
          onChange={(change) => onMode(change.target.value as Mode)}
          className="text-xs bg-background border border-border rounded-lg px-2 py-1 disabled:opacity-50"
        >
          <option value="fallback">{modeLabel('fallback')}</option>
          <option value="all">{modeLabel('all')}</option>
        </select>
      </div>

      {reachNeedsSaying(event) && (
        <p className="text-[11px] text-amber-500 mt-2" data-reach={reach}>
          {reachMessage(reach, coverage)}
        </p>
      )}

      {overridden.map((channel) => (
        <p key={channel} className="text-[11px] text-red-500 mt-1" data-overridden={channel}>
          {masterMessage('off', channel)}
        </p>
      ))}
    </div>
  )
}
